// FORGED Ed Systems — DEPLOY005 — v0.5.0 — api.ts
// Last touched: 2026-05-20
//
// Thin fetch wrappers for /api/* Netlify function endpoints.
// Every wrapper returns the standard { ok, data | message } envelope.
//
// DEPLOY005 — SaveIntakePayload now carries target_code/target_process/
// target_position instead of a single target_certification string.

import type { ApiResult } from "./types";

export async function postJson<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  try {
    const res = await fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        message: json.message || "Request failed (HTTP " + res.status + ")",
        where: json.where || path,
      };
    }
    return { ok: true, data: json.data as T };
  } catch (err) {
    var message = "unknown error";
    if (err instanceof Error) {
      message = err.message;
    }
    return { ok: false, message, where: path };
  }
}

export async function getJson<T>(path: string): Promise<ApiResult<T>> {
  try {
    const res = await fetch(path);
    const json = await res.json();
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        message: json.message || "Request failed (HTTP " + res.status + ")",
        where: json.where || path,
      };
    }
    return { ok: true, data: json.data as T };
  } catch (err) {
    var message = "unknown error";
    if (err instanceof Error) {
      message = err.message;
    }
    return { ok: false, message, where: path };
  }
}

// ---------- Endpoint wrappers ----------

export function healthCheck(): Promise<ApiResult<{ status: string; timestamp: string }>> {
  return getJson("/api/health-check");
}

export type RegisterPayload = {
  email: string;
  password: string;
  full_name: string;
  school_name: string;
  school_district: string | null;
  role: "instructor" | "program_director" | "cte_coordinator" | "other";
  heard_about_us_from: string | null;
  pilot_code: string | null;
  consent_marketing_email: boolean;
  consent_share_with_forgedweldscan: boolean;
};

export type RegisterResult = {
  account_id: string;
  email: string;
  pilot_code_redeemed: string | null;
  airtable_write: "ok" | "skipped" | "failed_nonblocking";
};

export function registerAccount(payload: RegisterPayload): Promise<ApiResult<RegisterResult>> {
  return postJson("/api/register-account", payload);
}

export type ValidatePilotCodeResult = {
  is_valid: boolean;
  granted_curriculum: string | null;
};

export function validatePilotCode(code: string): Promise<ApiResult<ValidatePilotCodeResult>> {
  return postJson("/api/validate-pilot-code", { code });
}

export type SaveIntakePayload = {
  submission_data: unknown;
  evaluation_curriculum: string;
  operating_mode: string;
  target_code: string;
  target_process: string;
  target_position: string;
};

export type SaveIntakeResult = {
  submission_id: string;
};

export async function saveIntake(payload: SaveIntakePayload): Promise<ApiResult<SaveIntakeResult>> {
  // Get the current session JWT to authenticate the request.
  const { supabase } = await import("./supabaseClient");
  const { data: sessionData } = await supabase.auth.getSession();
  const jwt = sessionData.session ? sessionData.session.access_token : null;
  if (!jwt) {
    return { ok: false, message: "Not signed in. Sign in and try again.", where: "saveIntake" };
  }
  try {
    const res = await fetch("/api/intake-save", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": "Bearer " + jwt,
      },
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        message: json.message || "Request failed (HTTP " + res.status + ")",
        where: json.where || "saveIntake",
      };
    }
    return { ok: true, data: json.data as SaveIntakeResult };
  } catch (err) {
    var message = "unknown error";
    if (err instanceof Error) {
      message = err.message;
    }
    return { ok: false, message, where: "saveIntake" };
  }
}

export type StartStrategyResult = {
  report_id: string;
  status: "queued" | "running" | "complete" | "failed";
};

export async function startStrategy(submissionId: string): Promise<ApiResult<StartStrategyResult>> {
  const { supabase } = await import("./supabaseClient");
  const { data: sessionData } = await supabase.auth.getSession();
  const jwt = sessionData.session ? sessionData.session.access_token : null;
  if (!jwt) {
    return { ok: false, message: "Not signed in.", where: "startStrategy" };
  }
  try {
    const res = await fetch("/api/start-strategy", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": "Bearer " + jwt,
      },
      body: JSON.stringify({ submission_id: submissionId }),
    });
    const json = await res.json();
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        message: json.message || "Request failed (HTTP " + res.status + ")",
        where: json.where || "startStrategy",
      };
    }
    return { ok: true, data: json.data as StartStrategyResult };
  } catch (err) {
    var message = "unknown error";
    if (err instanceof Error) {
      message = err.message;
    }
    return { ok: false, message, where: "startStrategy" };
  }
}

// Future endpoints:
//   - loadReport(reportId)  (currently handled by direct Supabase select via RLS)
//   - listMyReports()       (currently handled by direct Supabase select via RLS)
